/* iter-shelf1 — My Bookshelf carousel panel.
 *
 * A dockable workspace panel that shows the author's 3-stage pipeline
 * as a horizontally-scrollable book carousel:
 *
 *   Draft  ⇢  Finished  ⇢  Published
 *
 * Card CTAs by state:
 *   • Draft     → "Continue writing" (opens project in workspace)
 *                 + subtle "Mark as finished" pill when the backend
 *                   auto-suggests it (word-target hit + all chapters drafted)
 *   • Finished  → "Publish to Bookstore" (opens PublishToStoreDialog)
 *                 + "Back to draft" ghost link
 *   • Published → "$X earned · N sales" · "Boost with marketing"
 *                 (deep-link to /app/business#book-<id>)
 *
 * Uses horizontal scroll-snap (no new dep needed). One card per book.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PanelFrame } from '../workspace/PanelFrame';
import {
  BookOpen, PenLine, Rocket, TrendingUp, Sparkles, ChevronLeft, ChevronRight,
  BadgeCheck, Check, Loader2, Pencil, RotateCcw, DollarSign, ExternalLink,
  Zap, Trophy, BookMarked, ArrowUpDown, Download, ImagePlus, RefreshCw, Trash2,
  PackageCheck,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import PublishToStoreDialog from '../dialogs/PublishToStoreDialog';
import BookSalesSparkline from '../BookSalesSparkline';    // iter-shelf2
import AchievementList from '../AchievementBadge';         // iter-training-v-ach-coach
import { award, refreshFromServer } from '@/lib/achievements';
import { runTour } from '@/lib/tours';
import { startGlow, stopGlow } from '@/components/GlowNudge';
import { BookshelfReviewChip } from '../BookshelfReviewChip';   // Feb-2026 review timeline
import BookshelfOhasChip from '../BookshelfOhasChip';
import { coverArtApi, kdpExportApi, projectsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import PublishabilityDashboard from './PublishabilityDashboard';

/* ── KDP Keywords Modal ────────────────────────────────────────────────────── */
import { AuthorshipWarningModal } from '@/components/dialogs/AuthorshipWarningModal';

function KdpKeywordsModal({ projectId, onClose }) {
  const [keywords,   setKeywords]   = React.useState(Array(7).fill(''));
  const [loading,    setLoading]    = React.useState(true);
  const [exporting,  setExporting]  = React.useState(false);
  const [authOpen,   setAuthOpen]   = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await kdpExportApi.suggestKeywords(projectId);
        if (!cancelled) setKeywords((res.keywords || []).map(k => k || '').concat(Array(7).fill('')).slice(0, 7));
      } catch {
        // fallback — leave blanks so the author can fill manually
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const doDownload = async () => {
    setExporting(true);
    try {
      await kdpExportApi.download(projectId, keywords.filter(Boolean));
      toast.success('KDP export downloaded — authorship certificate included in ZIP.');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Export failed.');
    } finally { setExporting(false); }
  };

  // Show authorship warning modal first; only proceeds to download on confirm
  const handleDownload = () => setAuthOpen(true);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      data-testid="kdp-modal-overlay"
      onClick={onClose}
    >
      <div
        className="relative w-[440px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-background p-6 shadow-2xl"
        data-testid="kdp-keywords-modal"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-[15px] font-semibold text-foreground">KDP Keywords</h3>
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          AI-suggested keywords for your manuscript. Edit any field before downloading.
        </p>
        {loading ? (
          <div className="my-8 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[13px]">Analysing synopsis…</span>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            {keywords.map((kw, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-5 shrink-0 text-right text-[11px] text-muted-foreground">{i + 1}.</span>
                <input
                  value={kw}
                  onChange={e => setKeywords(prev => { const n = [...prev]; n[i] = e.target.value; return n; })}
                  placeholder={`Keyword ${i + 1}`}
                  maxLength={50}
                  className="flex-1 rounded border border-border bg-background px-2.5 py-1.5 text-[13px] outline-none focus:border-primary"
                  data-testid={`kdp-keyword-input-${i}`}
                />
              </div>
            ))}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-sm border border-border px-3 py-1.5 text-[12px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={exporting || loading}
            className="inline-flex items-center gap-1.5 rounded-sm bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
            data-testid="kdp-modal-download-btn"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            {exporting ? 'Building ZIP…' : 'Download ZIP'}
          </button>
        </div>
      </div>
      <AuthorshipWarningModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        projectId={projectId}
        onConfirmExport={doDownload}
      />
    </div>
  );
}

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STAGES = [
  { key: 'drafts',    label: 'Drafts',    icon: PenLine    },
  { key: 'finished',  label: 'Finished',  icon: BadgeCheck },
  { key: 'published', label: 'Published', icon: TrendingUp },
  { key: 'readiness', label: 'Readiness', icon: Trophy     },
];

const usd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

export function MyBookshelfCarousel({ onClose, maximized, onMaximize }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [stage, setStage]       = useState('drafts');
  const [sortKey, setSortKey]   = useState('last_edited'); // 'last_edited'|'words'|'completeness'|'title'
  const [publishFor, setPublishFor] = useState(null);   // { project_id, title, ... }
  const [markingId, setMarkingId]   = useState(null);
  const [reviewsByBook, setReviewsByBook] = useState({}); // Feb-2026: latest+history keyed by book_id
  const [publishDashOpen, setPublishDashOpen] = useState(false); // Publishability Dashboard slide-over
  // iter-training-v-ach-coach — achievement roll-up + celebration state.
  const [achievements, setAchievements] = useState({ map: {}, catalog: {} });
  const [celebrating,  setCelebrating]  = useState(null); // { key, meta }
  const scrollRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/authors/me/bookshelf`);
      setData(r.data);
    } catch (e) {
      // Silent — the panel just shows "no books yet" on 401/500.
      setData({ drafts: [], finished: [], published: [], suggest_finished_ids: [],
                counts: { drafts: 0, finished: 0, published: 0, total: 0 } });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // iter-storystart · Sep-2026 — Nudge the writer toward a live
  // editor pass once at least one manuscript is finished but not
  // yet published.
  useEffect(() => {
    if (data?.finished?.length > 0) {
      setTimeout(() => runTour('author-request-editor-review'), 500);
      startGlow('author-request-editor-review', 'activity-bookshelf-btn');
      return () => stopGlow('author-request-editor-review');
    }
    return undefined;
  }, [data]);

  // Feb-2026 — Pull the review timeline once so every card can paint
  // its own status chip without a per-book fetch. Silent on 401.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/authors/me/reader-reviews`);
        if (alive) setReviewsByBook(r.data?.by_book || {});
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  // Pull the awarded achievement list once so we can render badges on
  // book cards. Also refreshed when a new one is awarded.
  useEffect(() => {
    let alive = true;
    refreshFromServer().then((r) => { if (alive) setAchievements(r); });
    const onAwarded = () => {
      refreshFromServer().then((r) => { if (alive) setAchievements(r); });
    };
    window.addEventListener('pw-achievement-awarded', onAwarded);
    return () => { alive = false; window.removeEventListener('pw-achievement-awarded', onAwarded); };
  }, []);

  // iter-training-v-ach-coach — Award real-work milestones once the
  // bookshelf loads. Every call is idempotent (server + local cache).
  useEffect(() => {
    if (!data) return;
    const totalWords = [
      ...(data.drafts || []), ...(data.finished || []), ...(data.published || []),
    ].reduce((s, c) => s + (c.words || 0), 0);
    const hasAnyDrafted = [
      ...(data.drafts || []), ...(data.finished || []), ...(data.published || []),
    ].some((c) => (c.words || 0) > 0);
    const publishedCount = (data.published || []).length;
    const totalSales = (data.published || []).reduce(
      (s, c) => s + (c.book?.sales || 0), 0,
    );
    if (hasAnyDrafted)               award('milestone:first-chapter');
    if (totalWords >= 10000)         award('milestone:10k-words');
    if (totalWords >= 50000)         award('milestone:50k-words');
    if (totalWords >= 90000)         award('milestone:90k-words');
    if (publishedCount >= 1)         award('milestone:first-book');
    if (totalSales   >= 1)           award('milestone:first-sale');
    if (totalSales   >= 10)          award('milestone:10-sales');
    if (totalSales   >= 100)         award('milestone:100-sales');
  }, [data]);

  // Celebration toast whenever ANY new achievement lands.
  useEffect(() => {
    const onAwarded = (e) => {
      const meta = e?.detail?.meta;
      const key  = e?.detail?.key;
      if (!meta) return;
      setCelebrating({ key, meta });
      toast.success(`Achievement unlocked · ${meta.title}`, { duration: 3200 });
      setTimeout(() => setCelebrating(null), 3200);
    };
    window.addEventListener('pw-achievement-awarded', onAwarded);
    return () => window.removeEventListener('pw-achievement-awarded', onAwarded);
  }, []);

  const markFinished = async (pid) => {
    setMarkingId(pid);
    try {
      await axios.post(`${API}/authors/me/bookshelf/${pid}/mark-finished`);
      toast.success('Marked as finished — ready to publish.');
      await load();
      setStage('finished');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark finished.');
    } finally { setMarkingId(null); }
  };

  const markInProgress = async (pid) => {
    setMarkingId(pid);
    try {
      await axios.post(`${API}/authors/me/bookshelf/${pid}/mark-in-progress`);
      toast.success('Moved back to drafts.');
      await load();
      setStage('drafts');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update.');
    } finally { setMarkingId(null); }
  };

  const suggestSet = useMemo(
    () => new Set(data?.suggest_finished_ids || []),
    [data],
  );

  // Turn the awarded map into an array once for handing down.
  const awardedList = useMemo(() => {
    const catalog = achievements.catalog || {};
    return Object.keys(achievements.map || {})
      .map((k) => ({ key: k, meta: catalog[k] || { title: k, kind: 'other' } }));
  }, [achievements]);

  const cards = useMemo(() => {
    const raw = data ? (data[stage] || []) : [];
    return [...raw].sort((a, b) => {
      if (sortKey === 'last_edited') {
        return (b.last_touch || 0) > (a.last_touch || 0) ? 1 : -1;
      }
      if (sortKey === 'words') return (b.words || 0) - (a.words || 0);
      if (sortKey === 'completeness') {
        return (b.plot_readiness_score ?? -1) - (a.plot_readiness_score ?? -1);
      }
      if (sortKey === 'title') return (a.title || '').localeCompare(b.title || '');
      return 0;
    });
  }, [data, stage, sortKey]);

  // Two-row grid: scroll by ~2 columns at a time (170px × 2 + gap)
  const scrollBy = (dx) => scrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  return (
    <PanelFrame
      title="My Bookshelf"
      icon={BookOpen}
      onClose={onClose}
      onMaximize={onMaximize}
      maximized={maximized}
      testId="bookshelf-panel"
    >
      <div className="flex h-full flex-col" data-testid="bookshelf-panel-body">
        {/* Stage tabs */}
        <div className="flex items-center justify-between border-b border-panel-border bg-panel-header px-2 py-1.5">
          <div className="flex items-center gap-1" data-testid="bookshelf-stage-tabs">
            {STAGES.map(({ key, label, icon: Icon }) => {
              const count = data?.counts?.[key] ?? 0;
              const active = stage === key;
              return (
                <button
                  key={key}
                  onClick={() => setStage(key)}
                  data-testid={`bookshelf-tab-${key}`}
                  className={
                    'inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[13px] transition ' +
                    (active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground')
                  }
                >
                  <Icon className="h-3 w-3" />
                  {label}
                  <span className="rounded-sm bg-muted px-1 text-[9.5px] font-mono">{count}</span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1">
            {/* Publishability Dashboard trigger */}
            <button
              onClick={() => setPublishDashOpen(true)}
              className="inline-flex items-center gap-1 rounded-sm border border-emerald-300/70 bg-emerald-50/60 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
              data-testid="bookshelf-publishability-btn"
              title="Open Publishability Dashboard — novels scoring 85%+"
            >
              <PackageCheck className="h-3 w-3 shrink-0" />
              Publish-ready
            </button>
            {/* Sort control */}
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger
                className="h-6 gap-1 rounded border-transparent bg-transparent px-1.5 py-0 text-[13px] text-muted-foreground hover:bg-secondary hover:text-foreground focus:ring-0 [&>svg]:h-3 [&>svg]:w-3"
                data-testid="bookshelf-sort-select"
                title="Sort manuscripts"
              >
                <ArrowUpDown className="h-3 w-3 shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end" className="text-[13px]">
                <SelectItem value="last_edited" data-testid="sort-last-edited">Last edited</SelectItem>
                <SelectItem value="words"       data-testid="sort-words">Most words</SelectItem>
                <SelectItem value="completeness" data-testid="sort-completeness">Best completeness</SelectItem>
                <SelectItem value="title"       data-testid="sort-title">A–Z title</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => scrollBy(-320)}
              className="rounded-sm p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              data-testid="bookshelf-scroll-left"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => scrollBy(320)}
              className="rounded-sm p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              data-testid="bookshelf-scroll-right"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Auto-suggest banner */}
        {stage === 'drafts' && suggestSet.size > 0 && (
          <div
            className="border-b border-panel-border bg-primary/5 px-3 py-1.5 text-[13px] text-primary"
            data-testid="bookshelf-suggest-banner"
          >
            <Sparkles className="mr-1 inline h-3 w-3" />
            {suggestSet.size} book{suggestSet.size === 1 ? ' looks' : 's look'} ready to be marked finished.
          </div>
        )}

        {/* iter-training-v-ach-coach — Achievement roll-up strip. */}
        {awardedList.length > 0 && (
          <div
            className="border-b border-panel-border bg-amber-500/5 px-3 py-1.5"
            data-testid="bookshelf-achievements-row"
          >
            <div className="mb-0.5 flex items-center gap-1 text-[13px] uppercase tracking-wider text-amber-800/70 dark:text-amber-200/70">
              <Trophy className="h-2.5 w-2.5" />
              Your achievements · {awardedList.length}
            </div>
            <AchievementList
              items={awardedList}
              size="sm"
              max={8}
              testId="bookshelf-achievements"
            />
          </div>
        )}

        {/* Card row */}
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : stage === 'readiness' ? (
          <ReadinessDashboard data={data} />
        ) : cards.length === 0 ? (
          <EmptyStage stage={stage} />
        ) : (
          <div
            ref={scrollRef}
            className="flex-1 overflow-x-auto overflow-y-hidden"
            data-testid="bookshelf-scroll"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {/* Two-row CSS grid — flows items column-by-column so row 1 then row 2
                fill each column before moving right. Cards are ~1/4 the original
                cover scale, laid out horizontally: thumbnail left, info right. */}
            <div
              className="grid gap-2 p-3 h-full"
              style={{
                gridTemplateRows: 'repeat(2, auto)',
                gridAutoFlow: 'column',
                gridAutoColumns: '260px',
              }}
            >
              {cards.map((c) => (
                <BookCard
                  key={c.project_id}
                  card={c}
                  suggestFinished={suggestSet.has(c.project_id)}
                  markingId={markingId}
                  onMarkFinished={() => markFinished(c.project_id)}
                  onMarkInProgress={() => markInProgress(c.project_id)}
                  onPublish={() => setPublishFor(c)}
                  reviews={c.book?.book_id ? reviewsByBook[c.book.book_id] : null}
                  onDelete={() => load()}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {publishFor && (
        <PublishToStoreDialog
          card={publishFor}
          onClose={() => setPublishFor(null)}
          onPublished={() => { setPublishFor(null); load(); setStage('published'); }}
        />
      )}
      {/* Publishability Dashboard slide-over */}
      <PublishabilityDashboard
        open={publishDashOpen}
        onClose={() => setPublishDashOpen(false)}
      />
      {/* iter-training-v-ach-coach — Ephemeral celebration ribbon. */}
      {celebrating && (
        <div
          className="pointer-events-none absolute inset-x-0 top-8 z-10 flex justify-center"
          data-testid="bookshelf-celebration"
        >
          <div className="pointer-events-auto flex items-center gap-2 rounded-full border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-emerald-50 px-4 py-1.5 text-[13.5px] font-semibold text-amber-900 shadow-lg dark:text-amber-100">
            <Trophy className="h-3.5 w-3.5 text-amber-600" />
            Achievement unlocked · {celebrating.meta?.title}
          </div>
        </div>
      )}
    </PanelFrame>
  );
}

function EmptyStage({ stage }) {
  const copy = {
    drafts: {
      icon: PenLine,
      title: 'No drafts yet',
      body: 'Start a new novel from the Framework builder or the Templates picker.',
    },
    finished: {
      icon: BadgeCheck,
      title: 'No finished books',
      body: 'Mark a draft as finished when the manuscript is ready — it will appear here for one-click publishing.',
    },
    published: {
      icon: TrendingUp,
      title: 'Nothing on the shelf yet',
      body: 'Publish a finished book to the Bookstore and it will land here with live sales stats.',
    },
  }[stage] || { icon: BookOpen, title: 'Empty', body: '' };
  const Icon = copy.icon;
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center"
         data-testid={`bookshelf-empty-${stage}`}>
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="text-[13px] font-semibold">{copy.title}</div>
      <div className="max-w-xs text-[13px] text-muted-foreground">{copy.body}</div>
    </div>
  );
}

function PlotScoreBadge({ score, stale, missingCount }) {
  // Always render — show a faint "not analyzed" hint when no analysis has been run yet
  if (score == null) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-border/40 px-1.5 py-0 text-[9.5px] text-muted-foreground/50"
        title="Plot analysis has not been run for this manuscript"
        data-testid="bookshelf-plot-unanalyzed"
      >
        <BookMarked className="h-2.5 w-2.5 shrink-0" />
        Not analyzed
      </span>
    );
  }
  const colour = score >= 83
    ? 'bg-emerald-500/10 text-emerald-700 border-emerald-300/60 dark:text-emerald-300'
    : score >= 50
    ? 'bg-amber-500/10 text-amber-700 border-amber-300/60 dark:text-amber-300'
    : 'bg-rose-500/10 text-rose-700 border-rose-300/60 dark:text-rose-300';
  const tooltip = [
    `Plot structure: ${score}% complete`,
    stale ? ' (re-run suggested)' : '',
    missingCount ? ` · ${missingCount} missing element${missingCount !== 1 ? 's' : ''}` : '',
  ].join('');
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-full border px-1.5 py-0 text-[9.5px] font-semibold', colour)}
      title={tooltip}
      data-testid="bookshelf-plot-score-badge"
    >
      <BookMarked className="h-2.5 w-2.5 shrink-0" />
      {score}%{stale ? ' !' : ''}
      {missingCount > 0 && (
        <span className="rounded-full bg-current/15 px-1 font-normal">
          {missingCount} missing
        </span>
      )}
    </span>
  );
}

function BookCard({ card, suggestFinished, markingId, onMarkFinished, onMarkInProgress, onPublish, reviews, onDelete }) {
  const busy = markingId === card.project_id;
  const [boosting, setBoosting] = React.useState(false);
  const [generatingCover, setGeneratingCover] = React.useState(false);
  const [coverUrl, setCoverUrl] = React.useState(card.book?.cover_url || card.cover_url || null);
  const projectUrl = `/app/?project=${card.project_id}`;

  // ── Delete with inline confirm ────────────────────────────────────────────
  const [deleteStep, setDeleteStep] = React.useState('idle'); // 'idle' | 'confirm' | 'deleting'
  const requestDelete = (e) => { e.stopPropagation(); setDeleteStep('confirm'); };
  const cancelDelete  = (e) => { e.stopPropagation(); setDeleteStep('idle'); };
  const confirmDelete = async (e) => {
    e.stopPropagation();
    setDeleteStep('deleting');
    try {
      await projectsApi.remove(card.project_id);
      toast.success(`"${card.title}" deleted.`);
      onDelete?.(card.project_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete project.');
      setDeleteStep('idle');
    }
  };

  // ── Inline rename ────────────────────────────────────────────────────────
  const [displayTitle, setDisplayTitle] = React.useState(card.title);
  const [renaming,     setRenaming]     = React.useState(false);
  const [renameDraft,  setRenameDraft]  = React.useState('');
  const [renameSaving, setRenameSaving] = React.useState(false);

  const startRename = (e) => { e.stopPropagation(); setRenameDraft(displayTitle); setRenaming(true); };
  const cancelRename = () => { setRenaming(false); setRenameDraft(''); };

  const commitRename = async () => {
    const next = renameDraft.trim().slice(0, 200);
    if (!next || next === displayTitle) { cancelRename(); return; }
    setRenameSaving(true);
    try {
      await axios.post(`${API}/projects/${card.project_id}/global-rename`, { title: next });
      setDisplayTitle(next);
      cancelRename();
      toast.success('Manuscript renamed.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not rename manuscript.');
    } finally { setRenameSaving(false); }
  };

  const boost = async (e) => {
    e.stopPropagation();
    if (!card.book?.book_id) return;
    setBoosting(true);
    try {
      const r = await axios.post(`${API}/marketing/one-click-boost`, {
        book_id: card.book.book_id,
        platform_id: 'amazon-kdp-ads',
        amount_cents: 2500,
        duration_days: 7,
      });
      if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
      else toast.error('Boost started but no Stripe URL was returned.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start boost.');
    } finally { setBoosting(false); }
  };

  const generateCover = async (e) => {
    e.stopPropagation();
    setGeneratingCover(true);
    try {
      const res = await coverArtApi.generate(card.project_id);
      if (res?.cover_url) {
        setCoverUrl(res.cover_url);
        toast.success('Cover art generated!');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cover generation failed.');
    } finally { setGeneratingCover(false); }
  };

  const handleCardClick = () => { window.location.href = projectUrl; };

  return (
    <div
      onClick={handleCardClick}
      className="group flex cursor-pointer gap-3 rounded-md border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:shadow-panel-active"
      style={{ scrollSnapAlign: 'start' }}
      data-testid={`bookshelf-card-${card.project_id}`}
      title={`Open "${displayTitle}" in editor`}
    >
      {/* ── Cover thumbnail — matches Stacks h-24 w-16 ── */}
      <div
        className="relative h-24 w-16 shrink-0 overflow-hidden rounded-sm bg-secondary"
        data-testid={`bookshelf-cover-${card.project_id}`}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={displayTitle}
            className="h-full w-full object-cover transition group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <BookOpen className="h-5 w-5" />
          </div>
        )}
        {/* Generate/Regenerate cover button — appears on hover */}
        {!card.published && (
          <button
            onClick={generateCover}
            disabled={generatingCover}
            className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 opacity-0 transition-opacity hover:opacity-100"
            title={coverUrl ? 'Regenerate AI cover art' : 'Generate AI cover art'}
            data-testid={coverUrl ? `bookshelf-regen-cover-${card.project_id}` : `bookshelf-gen-cover-${card.project_id}`}
          >
            {generatingCover
              ? <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
              : coverUrl
                ? <RefreshCw className="h-3.5 w-3.5 text-white" />
                : <ImagePlus className="h-3.5 w-3.5 text-white" />}
            <span className="mt-0.5 text-[8px] font-medium text-white">
              {generatingCover ? 'Generating…' : coverUrl ? 'Regen' : 'AI Cover'}
            </span>
          </button>
        )}
        {/* Status badges — top-left, matching Stacks style */}
        {card.published && (
          <span
            className="absolute left-1 top-1 rounded-sm bg-emerald-600 px-1 py-0.5 text-[8.5px] font-semibold uppercase tracking-wider text-white"
            data-testid={`bookshelf-badge-live-${card.project_id}`}
          >Live</span>
        )}
        {!card.published && card.is_finished && (
          <span
            className="absolute left-1 top-1 rounded-sm bg-primary px-1 py-0.5 text-[8.5px] font-semibold uppercase tracking-wider text-primary-foreground"
            data-testid={`bookshelf-badge-final-${card.project_id}`}
          >Final</span>
        )}
        {!card.published && !card.is_finished && (
          <span
            className="absolute left-1 top-1 rounded-sm bg-primary px-1 py-0.5 text-[8.5px] font-semibold uppercase tracking-wider text-primary-foreground"
          >Draft</span>
        )}
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Title + rename */}
        <div className="min-w-0">
          {renaming ? (
            <div className="flex items-center gap-0.5">
              <input
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                  if (e.key === 'Escape') cancelRename();
                }}
                onBlur={commitRename}
                onClick={(e) => e.stopPropagation()}
                className="w-full rounded border border-primary px-1 py-0.5 text-[12px] font-semibold outline-none bg-background text-foreground"
                data-testid={`bookshelf-rename-input-${card.project_id}`}
              />
              {renameSaving && <Loader2 className="h-2.5 w-2.5 animate-spin text-muted-foreground shrink-0" />}
            </div>
          ) : (
            <div className="group/title flex items-start gap-0.5">
              <div
                className="line-clamp-2 flex-1 min-w-0 text-[13.5px] font-semibold leading-tight text-foreground"
                title={displayTitle}
                onDoubleClick={startRename}
              >
                {displayTitle}
              </div>
              {/* Rename button */}
              <button
                type="button"
                onClick={startRename}
                className="mt-px opacity-0 group-hover/title:opacity-100 shrink-0 rounded p-px text-muted-foreground hover:text-primary transition-opacity"
                title="Rename"
                data-testid={`bookshelf-rename-btn-${card.project_id}`}
              >
                <Pencil className="h-2 w-2" />
              </button>
              {/* Delete button — shows on hover, opens inline confirm */}
              {deleteStep === 'idle' && (
                <button
                  type="button"
                  onClick={requestDelete}
                  className="mt-px opacity-0 group-hover/title:opacity-100 shrink-0 rounded p-px text-muted-foreground hover:text-destructive transition-opacity"
                  title="Delete project"
                  data-testid={`bookshelf-delete-btn-${card.project_id}`}
                >
                  <Trash2 className="h-2 w-2" />
                </button>
              )}
            </div>
          )}
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            {card.author_name ? `by ${card.author_name}` : card.genre || '—'}
          </p>
        </div>

        {/* Plot score badge (compact) */}
        <PlotScoreBadge
          score={card.plot_readiness_score}
          stale={card.plot_readiness_stale}
          missingCount={card.plot_readiness_missing_count ?? 0}
        />

        {/* State-specific content */}
        <div className="mt-auto space-y-1 pt-1">

          {/* ── Inline delete confirm ─────────────────────────────────── */}
          {(deleteStep === 'confirm' || deleteStep === 'deleting') && (
            <div
              className="flex items-center gap-1 rounded border border-destructive/40 bg-destructive/5 px-1.5 py-1"
              onClick={(e) => e.stopPropagation()}
              data-testid={`bookshelf-delete-confirm-${card.project_id}`}
            >
              <span className="flex-1 text-[9.5px] text-destructive leading-tight">
                Delete permanently?
              </span>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteStep === 'deleting'}
                className="rounded px-1 py-0.5 text-[9px] font-semibold text-white bg-destructive hover:bg-destructive/90 disabled:opacity-60"
                data-testid={`bookshelf-delete-confirm-yes-${card.project_id}`}
              >
                {deleteStep === 'deleting' ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : 'Yes'}
              </button>
              <button
                type="button"
                onClick={cancelDelete}
                disabled={deleteStep === 'deleting'}
                className="rounded px-1 py-0.5 text-[9px] font-semibold text-muted-foreground hover:text-foreground"
                data-testid={`bookshelf-delete-confirm-no-${card.project_id}`}
              >
                No
              </button>
            </div>
          )}
          {card.published ? (
            <>
              <div className="flex items-center justify-between text-[9.5px]">
                <span className="text-muted-foreground">Earned</span>
                <span className="font-mono-stat font-semibold text-emerald-600">{usd(card.book.author_cents)}</span>
              </div>
              <div className="flex items-center justify-between text-[9.5px]">
                <span className="text-muted-foreground">Sales</span>
                <span className="font-mono-stat">{card.book.sales}</span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={boost}
                  disabled={boosting}
                  className="flex-1 inline-flex items-center justify-center gap-0.5 rounded-sm bg-primary py-0.5 text-[9px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60"
                  data-testid={`bookshelf-boost-${card.project_id}`}
                >
                  {boosting ? <Loader2 className="h-2 w-2 animate-spin" /> : <Zap className="h-2 w-2" />}
                  Boost
                </button>
                <a
                  href={`/bookstore/g/${card.book.genre_slug}`}
                  target="_blank" rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 inline-flex items-center justify-center gap-0.5 rounded-sm border border-border py-0.5 text-[9px] text-muted-foreground hover:text-foreground"
                  data-testid={`bookshelf-view-store-${card.project_id}`}
                >
                  <ExternalLink className="h-2 w-2" /> Store
                </a>
              </div>
            </>
          ) : card.is_finished ? (
            <>
              <div className="flex flex-wrap items-center gap-1 text-[10.5px] text-muted-foreground">
                {card.genre && <span className="rounded-sm bg-secondary px-1.5 py-0.5">{card.genre}</span>}
                <span className="font-mono">{(card.words || 0).toLocaleString()} w</span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onPublish(); }}
                className="w-full inline-flex items-center justify-center gap-0.5 rounded-sm bg-primary py-0.5 text-[9px] font-semibold text-primary-foreground hover:opacity-90"
                data-testid={`bookshelf-publish-${card.project_id}`}
              >
                <Rocket className="h-2 w-2" /> Publish
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onMarkInProgress(); }}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-0.5 rounded-sm border border-border py-0.5 text-[9px] text-muted-foreground hover:text-foreground disabled:opacity-50"
                data-testid={`bookshelf-unmark-${card.project_id}`}
              >
                {busy ? <Loader2 className="h-2 w-2 animate-spin" /> : <RotateCcw className="h-2 w-2" />}
                Back to draft
              </button>
            </>
          ) : (
            <>
              {/* Draft progress bar */}
              <div className="space-y-0.5">
                <div className="flex items-center justify-between text-[10.5px] text-muted-foreground">
                  <div className="flex flex-wrap items-center gap-1">
                    {card.genre && <span className="rounded-sm bg-secondary px-1.5 py-0.5">{card.genre}</span>}
                    <span className="font-mono">{(card.words || 0).toLocaleString()} w</span>
                  </div>
                  <span className="font-mono">{Math.round(card.pct_complete || 0)}%</span>
                </div>
                <div className="h-0.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${Math.min(100, card.pct_complete || 0)}%` }}
                  />
                </div>
              </div>
              {suggestFinished && (
                <button
                  onClick={(e) => { e.stopPropagation(); onMarkFinished(); }}
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-0.5 rounded-sm bg-emerald-500/10 py-0.5 text-[9px] font-semibold text-emerald-700 hover:bg-emerald-500/20 disabled:opacity-50"
                  data-testid={`bookshelf-mark-finished-${card.project_id}`}
                >
                  {busy ? <Loader2 className="h-2 w-2 animate-spin" /> : <BadgeCheck className="h-2 w-2" />}
                  Mark finished
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyBookshelfCarousel;

// ── Readiness Dashboard ────────────────────────────────────────────────────────
// Aggregates all novels ranked by publishability/readiness score.
// Source: fields added to bookshelf cards in routes_bookshelf.py
const READINESS_FILTERS = [
  { key: 'all',        label: 'All novels'          },
  { key: 'priority',   label: '85%+ Ready'          },
  { key: 'complete',   label: 'All 10 checks'       },
  { key: 'deviations', label: 'Has deviations'      },
];

function ReadinessDashboard({ data }) {
  const [filter, setFilter]   = useState('all');
  const [sortBy, setSortBy]   = useState('score'); // 'score' | 'title'

  // Collect all novels from all stages
  const allNovels = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.drafts    || []),
      ...(data.finished  || []),
      ...(data.published || []),
    ].filter(n => n.publishability_score != null || n.plot_readiness_score > 0);
  }, [data]);

  const filtered = useMemo(() => {
    let list = allNovels;
    if (filter === 'priority')   list = list.filter(n => n.publishability_priority);
    if (filter === 'complete')   list = list.filter(n => n.completeness_pass);
    if (filter === 'deviations') list = list.filter(n => (n.completeness_deviations || 0) > 0);
    return [...list].sort((a, b) => {
      if (sortBy === 'score') {
        const sa = a.publishability_score ?? a.plot_readiness_score ?? 0;
        const sb = b.publishability_score ?? b.plot_readiness_score ?? 0;
        return sb - sa;
      }
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [allNovels, filter, sortBy]);

  const readyCount = allNovels.filter(n => n.publishability_priority).length;

  if (allNovels.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center"
           data-testid="readiness-empty">
        <Trophy className="h-10 w-10 text-neutral-300" />
        <p className="text-[13px] font-semibold text-neutral-600">No readiness data yet</p>
        <p className="text-[11px] text-neutral-400 max-w-[220px]">
          Run the ZIP import pipeline or Plot Readiness on any novel to populate this dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="readiness-dashboard">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {READINESS_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-2.5 py-0.5 text-[10.5px] font-medium transition-colors ${
                filter === f.key
                  ? 'bg-oxblood-600 text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
              data-testid={`readiness-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {readyCount > 0 && (
          <span className="ml-auto text-[10px] font-semibold text-emerald-700">
            {readyCount} ready to publish
          </span>
        )}
      </div>

      {/* Novel list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="readiness-list">
        {filtered.length === 0 ? (
          <p className="py-8 text-center text-[12px] text-neutral-400">No novels match this filter.</p>
        ) : (
          filtered.map((novel) => (
            <ReadinessCard key={novel.project_id} novel={novel} />
          ))
        )}
      </div>
    </div>
  );
}

function ReadinessCard({ novel }) {
  const score    = novel.publishability_score ?? novel.plot_readiness_score ?? 0;
  const isPrio   = novel.publishability_priority;
  const devsOpen = novel.completeness_deviations || 0;
  const plMissing = novel.plot_readiness_missing_count || 0;
  const totalDevs = devsOpen + plMissing;
  const [kdpModalOpen, setKdpModalOpen] = React.useState(false);

  const scoreColor =
    score >= 85 ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
    : score >= 60 ? 'border-amber-400 bg-amber-50 text-amber-700'
    : 'border-rose-300 bg-rose-50 text-rose-700';

  // Theme / antagonist status badges
  const themeOk = novel.theme_completion_status === 'proven';
  const antOk   = novel.antagonist_arc_status   === 'complete';

  return (
    <div
      className={`rounded-lg border p-3 ${
        isPrio ? 'border-emerald-300 bg-emerald-50/30' : 'border-neutral-200 bg-white'
      }`}
      data-testid={`readiness-card-${novel.project_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Score circle */}
        <div className={`shrink-0 flex h-12 w-12 flex-col items-center justify-center rounded-full border-2 ${scoreColor}`}>
          <span className="text-[13px] font-bold leading-none">{score}</span>
          <span className="text-[7px] font-semibold uppercase tracking-wide mt-0.5">Score</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold text-[13px] text-neutral-900 truncate">{novel.title || 'Untitled'}</span>
            {isPrio && (
              <span className="shrink-0 rounded-full bg-emerald-600 px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wider text-white">
                Ready
              </span>
            )}
            {novel.completeness_pass && (
              <span className="shrink-0 rounded-full bg-violet-600 px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wider text-white">
                10/10
              </span>
            )}
          </div>

          {/* Status pills */}
          <div className="mt-1.5 flex flex-wrap gap-1">
            <StatusPill ok={themeOk} label="Theme" okLabel="proven" noLabel="unproven" />
            <StatusPill ok={antOk}   label="Antagonist" okLabel="resolved" noLabel="incomplete" />
            {totalDevs > 0 && (
              <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
                {totalDevs} deviation{totalDevs !== 1 ? 's' : ''} open
              </span>
            )}
          </div>
        </div>

        <a
          href={`/app/?project=${novel.project_id}`}
          className="shrink-0 rounded border border-neutral-200 p-1.5 text-neutral-500 hover:border-oxblood-400 hover:text-oxblood-600 transition-colors"
          title="Open in editor"
          data-testid={`readiness-open-${novel.project_id}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      {/* KDP Export button — only for 85%+ novels */}
      {score >= 85 && (
        <div className="mt-2 pt-2 border-t border-neutral-100">
          <button
            onClick={() => setKdpModalOpen(true)}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-700 transition-colors"
            data-testid={`readiness-kdp-export-${novel.project_id}`}
          >
            <Download className="h-3 w-3" /> Export for KDP
          </button>
        </div>
      )}
      {kdpModalOpen && (
        <KdpKeywordsModal
          projectId={novel.project_id}
          onClose={() => setKdpModalOpen(false)}
        />
      )}
    </div>
  );
}

function StatusPill({ ok, label, okLabel, noLabel }) {
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[9px] font-medium ${
      ok
        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
        : 'border-neutral-200 bg-neutral-50 text-neutral-500'
    }`}>
      {label}: {ok ? okLabel : noLabel}
    </span>
  );
}
